import { useMemo, type ReactNode } from 'react'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'
import { DateTime } from 'luxon'
import { parseAsTeamId, parseAsUserId } from '@util'
import { isEmpty, type CalendarTimeslotsInput, type TeamId, type UserId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { DateInput } from '@mantine/dates'
import { Group, Loader, Select } from '@mantine/core'
import { getTimeZones, useTimeslots } from '@front/model'
import { Page, TAlert, TeamInlineSelect, UserInlineSelect } from '@components'
import { TimeslotsDisplay } from './TimeslotsDisplay'

function CalendarCloserAvailability(): ReactNode {
  const [selectedTeam, setSelectedTeam] = useQueryState<TeamId>('teamId', parseAsTeamId)
  const [selectedUser, setSelectedUser] = useQueryState<UserId>('userId', parseAsUserId)

  const today = useMemo(() => DateTime.now().startOf('day').toMillis(), [])
  const [selectedDay, setSelectedDay] = useQueryState('day', parseAsInteger.withDefault(today))
  const dayFilter = useMemo(() => DateTime.fromMillis(selectedDay).toJSDate(), [selectedDay])
  const timeslotsInput: CalendarTimeslotsInput = useMemo(() => {
    return {
      targetId: selectedTeam ?? selectedUser ?? undefined,
      startTime: dayFilter.valueOf(),
      days: 2,
    }
  }, [selectedTeam, selectedUser, dayFilter])

  const { data: closers, isFetching, refetch } = useTimeslots(timeslotsInput)

  const [timezone, setTimezone] = useQueryState<string>('timezone', parseAsString)
  const userTimezone = useMemo(() => DateTime.now().zoneName, [])
  const currentValue = useMemo(() => {
    if (timezone) return timezone
    return userTimezone
  }, [timezone, userTimezone])

  const data = useMemo(() => {
    return getTimeZones(userTimezone)
  }, [userTimezone])

  return (
    <Page>
      <Page.Heading back title='Closer Availability' />
      <Page.FunctionBar>
        <Group>
          <DateInput
            allowDeselect={false}
            className='self-center'
            type='default'
            value={dayFilter}
            onChange={(value) => {
              if (value) {
                setSelectedDay(DateTime.fromJSDate(new Date(value)).plus({ day: 1 }).startOf('day').toMillis())
              } else {
                setSelectedDay(null)
              }
            }}
          />
          <TeamInlineSelect
            allowDeselect
            value={selectedTeam || undefined}
            placeholder='Select team'
            onChange={(value) => {
              setSelectedUser(null)
              setSelectedTeam(value ?? null)
            }}
          />
          <UserInlineSelect
            allowDeselect
            value={selectedUser || undefined}
            placeholder='Select user'
            onChange={(value) => {
              setSelectedTeam(null)
              setSelectedUser(value ?? null)
            }}
          />
          <Select data={data} value={currentValue} onChange={(value) => setTimezone(value ?? null)} />
        </Group>
      </Page.FunctionBar>

      <Page.Content scrollArea containerProps={{ className: 'pt-xl' }}>
        {isFetching && <Loader />}
        {!isFetching &&
          (isEmpty(closers) ? (
            <TAlert variant='warning' title='No closer timeslots available' />
          ) : (
            <TimeslotsDisplay closers={closers} filter={DateTime.fromJSDate(dayFilter)} timezone={currentValue} />
          ))}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/calendar_/closerAvailability/')({
  component: CalendarCloserAvailability,
})
